import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Email, Groups, Refresh } from '@mui/icons-material';
import moment from 'moment';
import getSimulationConversation from 'redux/actions/Common/getSimulationConversation';

const PAGE_SIZE = 20;
const BODY_CLAMP_LINES = 8;

const joinEmails = (list) => (list || []).filter(Boolean).join(', ');

const ConversationEntry = ({ entry, nameFor }) => {
  const [expanded, setExpanded] = useState(false);
  const isMeeting = entry?.type === 'meeting_recap';
  const body = entry?.body || '';
  const isLong =
    body.split('\n').length > BODY_CLAMP_LINES || body.length > 800;

  const participant = (email) => {
    const name = nameFor(email);
    return name ? `${name} <${email}>` : email;
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box
        display={'flex'}
        alignItems={'flex-start'}
        justifyContent={'space-between'}
        gap={1}
      >
        <Box display={'flex'} alignItems={'center'} gap={1} minWidth={0}>
          {isMeeting ? (
            <Groups fontSize="small" color="action" />
          ) : (
            <Email fontSize="small" color="action" />
          )}
          <Typography variant={'body2'} fontWeight={'bold'} noWrap>
            {entry?.subject || (isMeeting ? 'Meeting recap' : '(no subject)')}
          </Typography>
        </Box>
        <Tooltip
          title={
            entry?.createdOn
              ? moment(entry.createdOn).format('DD MMMM YYYY, hh:mm:ss A')
              : ''
          }
        >
          <Typography
            variant={'caption'}
            color={'text.secondary'}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {entry?.createdOn
              ? moment(entry.createdOn).format('DD MMM, hh:mm A')
              : '--'}
          </Typography>
        </Tooltip>
      </Box>

      <Box mt={0.5}>
        {entry?.from ? (
          <Typography variant={'caption'} display={'block'}>
            <b>From:</b> {participant(entry.from)}
          </Typography>
        ) : null}
        {entry?.to?.length ? (
          <Typography variant={'caption'} display={'block'}>
            <b>To:</b> {(entry.to || []).map(participant).join(', ')}
          </Typography>
        ) : null}
        {entry?.cc?.length ? (
          <Typography variant={'caption'} display={'block'}>
            <b>Cc:</b> {joinEmails(entry.cc.map(participant))}
          </Typography>
        ) : null}
        {entry?.bcc?.length ? (
          <Typography variant={'caption'} display={'block'}>
            <b>Bcc:</b> {joinEmails(entry.bcc.map(participant))}
          </Typography>
        ) : null}
        {entry?.attendees?.length ? (
          <Typography variant={'caption'} display={'block'}>
            <b>Attendees:</b> {joinEmails(entry.attendees.map(participant))}
          </Typography>
        ) : null}
      </Box>

      <Box display={'flex'} gap={0.5} mt={1} flexWrap={'wrap'}>
        <Chip
          size="small"
          variant="outlined"
          label={isMeeting ? 'meeting recap' : 'email'}
        />
        {entry?.classification ? (
          <Chip
            size="small"
            variant="outlined"
            color={entry.classification === 'external' ? 'primary' : 'default'}
            label={entry.classification}
          />
        ) : null}
        {entry?.loggedAsInteraction ? (
          <Chip
            size="small"
            variant="outlined"
            color="success"
            label="logged as interaction"
          />
        ) : null}
      </Box>

      {body ? (
        <>
          <Typography
            variant={'body2'}
            mt={1}
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              ...(expanded || !isLong
                ? {}
                : {
                    display: '-webkit-box',
                    WebkitLineClamp: BODY_CLAMP_LINES,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }),
            }}
          >
            {body}
          </Typography>
          {isLong ? (
            <Button
              size="small"
              sx={{ px: 0, mt: 0.5 }}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? 'Show less' : 'Show more'}
            </Button>
          ) : null}
        </>
      ) : null}
    </Paper>
  );
};

// The shared buyer↔seller thread — what either side would actually read —
// rendered like an inbox rather than the per-agent reasoning log.
const SimulationConversation = ({ simulationId, agents }) => {
  const dispatch = useDispatch();

  const [entries, setEntries] = useState([]);
  const [nextLastSeen, setNextLastSeen] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const requestSeq = useRef(0);

  const fetchPage = (lastSeen) => {
    const seq = ++requestSeq.current;
    if (lastSeen) setFetchingMore(true);
    else setFetching(true);
    setError(null);
    dispatch(
      getSimulationConversation(
        simulationId,
        PAGE_SIZE,
        lastSeen || null,
        (data) => {
          if (seq !== requestSeq.current) return;
          const page = data?.entries || [];
          setEntries((existing) => {
            if (!lastSeen) return page;
            const seen = new Set(existing.map((entry) => entry?.id));
            return [...existing, ...page.filter((it) => !seen.has(it?.id))];
          });
          setNextLastSeen(data?.nextLastSeen || null);
          setFetching(false);
          setFetchingMore(false);
        },
        (message) => {
          if (seq !== requestSeq.current) return;
          setError(message);
          setFetching(false);
          setFetchingMore(false);
        },
      ),
    );
  };

  useEffect(() => {
    setEntries([]);
    setNextLastSeen(null);
    fetchPage(null);
  }, [simulationId]);

  const namesByEmail = (agents || []).reduce((acc, agent) => {
    if (agent?.email) acc[agent.email.toLowerCase()] = agent?.name;
    return acc;
  }, {});
  const nameFor = (email) => namesByEmail[email?.toLowerCase()];

  const rows = entries.filter((entry) =>
    typeFilter === 'all'
      ? true
      : typeFilter === 'meeting_recap'
      ? entry?.type === 'meeting_recap'
      : entry?.type !== 'meeting_recap',
  );

  return (
    <Box display={'flex'} flexDirection={'column'} gap={1.5}>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        flexWrap={'wrap'}
        gap={1}
      >
        <Typography variant={'body2'} color={'text.secondary'}>
          Every email and meeting recap exchanged in this simulation, oldest
          first.{' '}
          {entries.length > 0
            ? `${entries.length} loaded${nextLastSeen ? ' so far' : ''}.`
            : ''}
        </Typography>
        <Box display={'flex'} alignItems={'center'} gap={1}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={typeFilter}
            onChange={(e, value) => {
              if (value) setTypeFilter(value);
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="email">Emails</ToggleButton>
            <ToggleButton value="meeting_recap">Meetings</ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            onClick={() => fetchPage(null)}
            disabled={fetching}
            startIcon={<Refresh fontSize="small" />}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => fetchPage(null)}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      {fetching ? (
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          gap={1}
          py={6}
        >
          <CircularProgress size={20} />
          <Typography variant={'body2'} color={'text.secondary'}>
            Loading conversation...
          </Typography>
        </Box>
      ) : entries.length === 0 && !error ? (
        <Typography
          variant={'body2'}
          color={'text.secondary'}
          textAlign={'center'}
          py={6}
        >
          No emails or meetings exchanged yet.
        </Typography>
      ) : (
        <>
          {rows.map((entry) => (
            <ConversationEntry
              key={entry?.id}
              entry={entry}
              nameFor={nameFor}
            />
          ))}
          {rows.length === 0 ? (
            <Typography
              variant={'body2'}
              color={'text.secondary'}
              textAlign={'center'}
              py={2}
            >
              No loaded entries match this filter.
            </Typography>
          ) : null}
          {nextLastSeen ? (
            <Box display={'flex'} justifyContent={'center'} p={'10px'}>
              <Button
                size="small"
                variant="contained"
                onClick={() => fetchPage(nextLastSeen)}
                disabled={fetchingMore}
                sx={{ whiteSpace: 'nowrap', minWidth: 100 }}
              >
                {fetchingMore ? (
                  <>
                    Loading more ...
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  </>
                ) : (
                  'Load More'
                )}
              </Button>
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
};

export default SimulationConversation;
